import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { get } from 'lodash-es';
import classnames from 'classnames';
import { Col, Container, Row } from 'reactstrap';
import { pageDefinition } from 'site-modules/shared/pages/page';
import { EdmundsCpaNoAdhesionDecorator } from 'site-modules/shared/pages/decorators/edmunds-cpa-no-adhesion';
import { TrackingHandler } from 'client/tracking/handler';
import { TrackingMap } from 'client/tracking/maps/dealerships/dealer-review';
import { SITE_URL } from 'site-modules/shared/pages/seo';
import { DealerInfoEntities, DealerInfoModel, getDealerPath } from 'client/data/models/dealer-info';
import { connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { DEALER_PROPS, REVIEW_TITLE } from 'client/constants/dealer-reviews';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { DealerReviewDetails } from 'site-modules/dealerships/components/dealer-review-details/dealer-review-details';
import { DealerReviewForm } from 'client/site-modules/dealerships/components/dealer-review-form/dealer-review-form';
import { ReviewsModel, ReviewsPaths } from 'client/data/models/dealer-reviews';
import { MrectNative } from 'site-modules/shared/components/native-ad/mrect/mrect-native';
import { getModelState } from 'client/data/luckdragon/model';
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { getBaseBreadcrumb } from 'site-modules/shared/components/breadcrumbs/helper';
import { TrackingConstant } from 'client/tracking/constant';
import { EventToolbox } from 'client/utils/event-toolbox';
import { scrollToTop } from 'client/utils/scroll';
import { redirect } from 'client/actions/redirect';
import { getDealerReviewPageBreadcrumbs } from 'client/site-modules/dealerships/utils/breadcrumbs';
import { DealerReviewThankYou } from 'site-modules/dealerships/components/dealer-review-form/dealer-review-thank-you';
import { MRECT_WIRED_CUSTOM_SIZE } from 'site-modules/shared/components/ad-unit/context/sizes';

export class DealerReviewUI extends Component {
  static propTypes = {
    params: DEALER_PROPS.isRequired,
    reviewType: PropTypes.string.isRequired,
    isMobile: PropTypes.bool,
    dealerInfo: DealerInfoEntities.DealerInfo,
  };

  static defaultProps = {
    isMobile: false,
    dealerInfo: {},
  };

  state = {
    isThankYouPage: false,
  };

  componentDidMount() {
    TrackingHandler.useMap(TrackingMap);
  }

  displayThankYou = () => {
    this.setState({ isThankYouPage: true }, () => {
      scrollToTop();
    });

    EventToolbox.fireTrackAction({
      event_type: 'widget_view',
      event_data: {
        action_name: 'widget_view',
        action_cause: TrackingConstant.SUBMIT,
        creative_id: 'one_final_step',
      },
    });
  };

  render() {
    const { isMobile, reviewType, dealerInfo, params } = this.props;
    const { isThankYouPage } = this.state;

    if (!get(dealerInfo, 'id')) return null;

    const rightRail = (
      <Col xs={12} lg={4} className={classnames({ 'mt-2': !isThankYouPage })}>
        {isMobile && <hr />}
        <div className="mb-3_5">
          <MrectNative position={'1'} refreshable={false} dfpCollapse />
        </div>
        <div className="pt-md-3_5 mb-2">
          <MrectNative
            position={'2'}
            refreshable={false}
            wiredOnly
            addCustomSizesWired={MRECT_WIRED_CUSTOM_SIZE}
            dfpCollapse
          />
        </div>
      </Col>
    );
    const breadcrumbs = getDealerReviewPageBreadcrumbs({ dealerInfo, reviewType });

    if (isThankYouPage) {
      breadcrumbs.push(getBaseBreadcrumb('Review Almost Done', ''));
    }

    return (
      <Container
        tag="main"
        id="main-content"
        tabIndex={-1}
        className={classnames('dealer-review', reviewType)}
        data-tracking-parent="dealer_review"
      >
        <Breadcrumbs className="ps-0" data={breadcrumbs} />
        {isThankYouPage ? (
          <DealerReviewThankYou>{rightRail}</DealerReviewThankYou>
        ) : (
          <Fragment>
            <h1 className="heading-2 my-1_5">{dealerInfo.name}</h1>
            <DealerReviewDetails dealerInfo={dealerInfo} reviewType={reviewType} params={params} />
            <Row>
              <Col xs={12} lg={8} className="mt-2">
                <DealerReviewForm
                  reviewType={reviewType}
                  dealerName={dealerInfo.name}
                  dealerId={dealerInfo.id}
                  stateCode={get(dealerInfo, 'address.stateCode', '')}
                  displayThankYou={this.displayThankYou}
                />
              </Col>
              {rightRail}
            </Row>
          </Fragment>
        )}
      </Container>
    );
  }
}

export const mapStateToProps = state => ({
  isMobile: state.mobile,
});

export const stateToPropsConfig = {
  dealerInfo: {
    path: props => getDealerPath(props),
    segment: DealerInfoModel,
  },
};

export const DealerReview = withRouter(connect(mapStateToProps)(connectToModel(DealerReviewUI, stateToPropsConfig)));

export default params =>
  pageDefinition({
    name: `car_inventory_ddp_simplified_${params.reviewType}_reviews_form`,
    category: 'car_inventory',
    preload: (store, props) => {
      const { mobile } = store.getState();
      const preloader = new ModelPreloader(store);
      const dealerPath = getDealerPath({ ...props, params });

      preloader.resolve(dealerPath, DealerInfoModel);

      return preloader.load().then(modelState => {
        const dealer = modelState.get(dealerPath, DealerInfoModel);

        if (!dealer) {
          store.dispatch(redirect({ path: '/dealerships/' }));
          return preloader.load(); // short circuit here, because we are going to redirect (so no point in continuing)
        }

        preloader.resolve(ReviewsPaths.getOverallRatings({ dealerId: dealer.rooftopId }), ReviewsModel);

        if (mobile) {
          preloader.resolve(`carcodeConfig.${dealer.rooftopId}`, DealerInfoModel);
        }

        return preloader.load();
      });
    },
    seo: (state, props) => {
      const { reviewType } = params;
      const canonical = `${SITE_URL}${props.location.pathname}`;
      const type = REVIEW_TITLE[reviewType];
      const modelState = getModelState(state);
      const dealerInfo = modelState.get(getDealerPath({ ...props, params }), DealerInfoModel);

      if (!dealerInfo) {
        return {};
      }

      const dealerName = dealerInfo.name;

      return {
        title: `${type} Review for ${dealerName} | Edmunds`,
        canonical,
        description: `Research and Write ${type} Reviews for ${dealerName} on Edmunds.com`,
        robots: {
          follow: true,
          noIndex: true,
        },
      };
    },
    component: props => <DealerReview {...props} reviewType={params.reviewType} params={params} />,
    decorator: EdmundsCpaNoAdhesionDecorator,
  });
